import { motion } from "framer-motion";
import {
  Briefcase,
  Workflow,
  FileSearch,
  MapPin,
  Share2,
  Globe,
  Inbox,
  CalendarClock,
  Sparkles,
  ArrowUpRight,
  Zap,
} from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Link } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import SectionHeader from "@/components/shared/SectionHeader";
import StatCard from "@/components/shared/StatCard";
import { Button } from "@/components/ui/button";
import {
  DASHBOARD_METRICS,
  REVENUE_TREND,
  SOURCE_MIX,
  RECENT_ACTIVITY,
} from "@/data/mockData";

const MODULES = [
  { to: "/freelance", label: "Freelance Finder",  desc: "Upwork · LinkedIn Jobs",         icon: Briefcase,      accent: "indigo",  stat: "128 new" },
  { to: "/leads",     label: "Lead Engine",       desc: "n8n outreach workflow",           icon: Workflow,       accent: "cyan",    stat: "7 flows" },
  { to: "/rfp",       label: "RFP Intelligence",  desc: "Gov. procurement watcher",        icon: FileSearch,     accent: "amber",   stat: "24 active" },
  { to: "/lead-finder", label: "Lead Finder",     desc: "Local business audit",            icon: MapPin,         accent: "emerald", stat: "1.2k found" },
  { to: "/social",    label: "Social Automation", desc: "LinkedIn + Instagram",            icon: Share2,         accent: "pink",    stat: "96 prospects" },
  { to: "/website",   label: "Website Intel",     desc: "Live visitors + forms",           icon: Globe,          accent: "cyan",    stat: "42 today" },
  { to: "/email",     label: "Email Intel",       desc: "Inbox AI qualification",          icon: Inbox,          accent: "indigo",  stat: "6 hot" },
  { to: "/meetings",  label: "Meetings Intel",    desc: "Calendly + booking pre-brief",    icon: CalendarClock,  accent: "amber",   stat: "5 upcoming" },
];

export default function Dashboard() {
  return (
    <div data-testid="dashboard-page" className="spotlight-indigo -mx-6 lg:-mx-10 -my-8 lg:-my-10 px-6 lg:px-10 py-8 lg:py-10">
      <PageHeader
        eyebrow="Command Center"
        title={
          <>
            Every deal.<br />
            <span className="bg-gradient-to-r from-indigo-300 via-white to-cyan-200 bg-clip-text text-transparent">
              One intelligent OS.
            </span>
          </>
        }
        description="Base2Brand's five sales engines run in parallel — surfacing the highest-signal opportunities across freelance platforms, government procurement, local business intelligence, and social outreach."
        actions={
          <>
            <Button
              data-testid="dashboard-run-flow"
              className="rounded-full bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] text-white"
            >
              <Zap className="h-3.5 w-3.5 mr-1.5" />
              Run all flows
            </Button>
            <Button
              data-testid="dashboard-new-proposal"
              className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              New AI proposal
            </Button>
          </>
        }
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard testId="stat-new-jobs" label="New Jobs Today" value={DASHBOARD_METRICS.newJobsToday} delta="+18%" accent="indigo" icon={Briefcase} />
        <StatCard testId="stat-high-value" label="High Value Jobs" value={DASHBOARD_METRICS.highValueJobs} delta="+9%" accent="cyan" icon={ArrowUpRight} />
        <StatCard testId="stat-win-rate" label="Win Rate" value={DASHBOARD_METRICS.proposalWinRate} suffix="%" delta="+4.2pt" accent="emerald" />
        <StatCard testId="stat-est-revenue" label="Est. Revenue" value="486k" prefix="$" delta="+22%" accent="amber" />
        <StatCard testId="stat-ai-match" label="Avg AI Match" value={DASHBOARD_METRICS.aiMatch} suffix="%" delta="+3.1pt" accent="pink" />
        <StatCard testId="stat-active-rfps" label="Active RFPs" value={DASHBOARD_METRICS.activeRFPs} delta="+2" accent="cyan" icon={FileSearch} />
      </div>

      {/* Row 2: Revenue + Source Mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div data-testid="dashboard-revenue-chart" className="glass-card relative overflow-hidden lg:col-span-2 p-6">
          <SectionHeader
            kicker="Pipeline Value · last 6 months"
            title="Revenue Trend"
            action={
              <span className="font-mono text-xs text-white/50">
                <span className="text-emerald-400">▲ 132%</span> vs prior period
              </span>
            }
          />
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={REVENUE_TREND} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#5E6AD2" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#5E6AD2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  contentStyle={{
                    background: "#0d0d10",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8,
                    fontFamily: "JetBrains Mono",
                    fontSize: 11,
                    color: "#EDEDED",
                  }}
                  labelStyle={{ color: "#A1A1AA" }}
                  cursor={{ stroke: "#5E6AD2", strokeOpacity: 0.35 }}
                />
                <Area type="monotone" dataKey="v" stroke="#8B9AFF" strokeWidth={2} fill="url(#revGrad)" />
                <Line type="monotone" dataKey="v" stroke="#00E5FF" strokeWidth={0} dot={{ r: 3, fill: "#00E5FF" }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="dashboard-source-mix" className="glass-card p-6">
          <SectionHeader kicker="Where opportunities come from" title="Source Mix" />
          <div className="flex items-center gap-6">
            <div className="h-40 w-40 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={SOURCE_MIX}
                    innerRadius={44}
                    outerRadius={72}
                    dataKey="value"
                    stroke="none"
                    paddingAngle={3}
                  >
                    {SOURCE_MIX.map((s) => (
                      <Cell key={s.name} fill={s.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="font-mono text-2xl">1,284</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">this month</div>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              {SOURCE_MIX.map((s) => (
                <div key={s.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                    <span className="text-white/70">{s.name}</span>
                  </div>
                  <span className="font-mono text-white/60">{s.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Module grid */}
      <SectionHeader className="mt-12" kicker="Five engines. One CRM." title="Modules" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {MODULES.map((m, i) => (
          <ModuleCard key={m.to} module={m} index={i} />
        ))}
      </div>

      {/* Activity */}
      <SectionHeader className="mt-12" kicker="Live" title="Recent automation activity" />
      <div className="glass-card p-2 divide-y divide-white/[0.05]">
        {RECENT_ACTIVITY.map((a) => (
          <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-center gap-4 px-4 py-3">
            <span className={`h-2 w-2 rounded-full ${
              a.tone === "ai" ? "bg-indigo-400 shadow-[0_0_8px_#5E6AD2]" :
              a.tone === "flow" ? "bg-cyan-400 shadow-[0_0_8px_#00e5ff]" :
              a.tone === "rfp" ? "bg-amber-400 shadow-[0_0_8px_#f59e0b]" :
              a.tone === "maps" ? "bg-emerald-400 shadow-[0_0_8px_#10b981]" :
              "bg-pink-400 shadow-[0_0_8px_#ec4899]"
            }`} />
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] text-white/85">
                <span className="text-white/60">{a.who}</span> · {a.what}
              </div>
            </div>
            <div className="font-mono text-[11px] text-white/40">{a.when}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModuleCard({ module, index }) {
  const Icon = module.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * index, duration: 0.35 }}
    >
      <Link
        to={module.to}
        data-testid={`module-card-${module.to.slice(1)}`}
        className="group glass-card block p-5 hover:translate-y-[-2px] transition-transform"
      >
        <div className="flex items-start justify-between">
          <div className={`h-10 w-10 rounded-xl bg-white/[0.03] border border-white/[0.06] grid place-items-center text-${module.accent === "indigo" ? "indigo" : module.accent}-300`}>
            <Icon className="h-4 w-4" />
          </div>
          <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-white transition-colors" />
        </div>
        <div className="mt-4 font-display text-lg text-white">{module.label}</div>
        <div className="text-[13px] text-white/50">{module.desc}</div>
        <div className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-white/60 border border-white/[0.06] bg-white/[0.02] rounded-full px-2.5 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          {module.stat}
        </div>
      </Link>
    </motion.div>
  );
}
